import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const chartContainerRef = useRef(null);

  useEffect(() => {
    fetchStats();
    loadTradingViewWidget();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/trades/stats/summary`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadTradingViewWidget = () => {
    if (chartContainerRef.current && !chartContainerRef.current.querySelector('script')) {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      script.type = 'text/javascript';
      script.async = true;
      script.innerHTML = JSON.stringify({
        autosize: true,
        symbol: "CME_MINI:ES1!",
        interval: "60",
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        locale: "en",
        enable_publishing: false,
        backgroundColor: "rgba(5, 5, 5, 1)",
        gridColor: "rgba(38, 38, 38, 0.5)",
        hide_top_toolbar: false,
        hide_legend: false,
        save_image: false,
        calendar: false,
        hide_volume: false,
        support_host: "https://www.tradingview.com"
      });
      chartContainerRef.current.appendChild(script);
    }
  };

  const StatCard = ({ label, value, subValue, trend, color = "blue" }) => (
    <div className="card-institutional p-4 relative overflow-hidden group">
      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-transparent to-blue-500/5 transform rotate-45 translate-x-8 -translate-y-8" />
      <p className="text-xs font-mono text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-mono font-bold ${
        color === "green" ? "text-emerald-500" : 
        color === "red" ? "text-red-500" : 
        "text-white"
      }`}>
        {value}
      </p>
      {subValue && (
        <p className={`text-xs font-mono mt-1 ${
          trend === "up" ? "text-emerald-500" : 
          trend === "down" ? "text-red-500" : 
          "text-slate-500"
        }`}>
          {subValue}
        </p>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-bold text-white">COMMAND CENTER</h1>
          <p className="text-sm text-slate-500 font-mono">Monitor. Analyze. Execute.</p>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono text-slate-500">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          MARKET LIVE
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard 
          label="TOTAL TRADES" 
          value={stats?.total_trades || 0}
        />
        <StatCard 
          label="WIN RATE" 
          value={`${stats?.win_rate || 0}%`}
          color={stats?.win_rate >= 50 ? "green" : "red"}
        />
        <StatCard 
          label="TOTAL P&L" 
          value={`$${stats?.total_pnl?.toLocaleString() || 0}`}
          color={stats?.total_pnl >= 0 ? "green" : "red"}
          trend={stats?.total_pnl >= 0 ? "up" : "down"}
        />
        <StatCard 
          label="AVG R:R" 
          value={stats?.avg_rr || "0"}
          subValue="Target: 1:3+"
        />
        <StatCard 
          label="CHECKLIST %" 
          value={`${stats?.checklist_compliance || 0}%`}
          subValue="Compliance"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart - Takes 2 columns */}
        <div className="lg:col-span-2">
          <div className="card-institutional h-[500px] overflow-hidden">
            <div className="p-4 border-b border-[#262626] flex items-center justify-between">
              <div>
                <h2 className="font-mono text-sm font-bold text-white">FUTURES CHART</h2>
                <p className="text-xs text-slate-500 font-mono">E-mini S&P 500</p>
              </div>
              <div className="flex gap-2">
                {["ES", "NQ", "CL", "GC"].map((symbol) => (
                  <button
                    key={symbol}
                    className="px-3 py-1 text-xs font-mono bg-[#171717] border border-[#262626] text-slate-400 hover:text-white hover:border-blue-500/50 transition-colors"
                    data-testid={`symbol-${symbol}`}
                  >
                    {symbol}
                  </button>
                ))}
              </div>
            </div>
            <div className="tradingview-widget-container h-[calc(100%-60px)]" ref={chartContainerRef}>
              <div className="tradingview-widget-container__widget h-full" />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card-institutional p-4">
            <h3 className="font-mono text-sm font-bold text-white mb-4">PERFORMANCE</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Best Trade</span>
                <span className="font-mono text-emerald-500">${stats?.best_trade || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Worst Trade</span>
                <span className="font-mono text-red-500">${stats?.worst_trade || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Winning Trades</span>
                <span className="font-mono text-white">{stats?.winning_trades || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Losing Trades</span>
                <span className="font-mono text-white">{stats?.losing_trades || 0}</span>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="card-institutional p-4">
            <h3 className="font-mono text-sm font-bold text-white mb-4">INSTITUTIONAL RULES</h3>
            <div className="space-y-3">
              {[
                "Never risk more than 1% per trade",
                "Wait for liquidity sweep before entry",
                "Always confirm with FU candle close",
                "Target minimum 1:3 R:R ratio",
                "Complete checklist before every trade",
              ].map((rule, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-4 h-4 mt-0.5 border border-blue-500/50 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-500 text-xs font-mono">{i + 1}</span>
                  </div>
                  <p className="text-xs text-slate-400">{rule}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Market Ticker */}
          <div className="card-institutional overflow-hidden">
            <div className="p-4 border-b border-[#262626]">
              <h3 className="font-mono text-sm font-bold text-white">MARKET OVERVIEW</h3>
            </div>
            <div className="h-[200px]">
              <div className="tradingview-widget-container h-full">
                <iframe
                  src="https://s.tradingview.com/embed-widget/market-overview/?locale=en#%7B%22colorTheme%22%3A%22dark%22%2C%22dateRange%22%3A%2212M%22%2C%22showChart%22%3Afalse%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22largeChartUrl%22%3A%22%22%2C%22isTransparent%22%3Atrue%2C%22showSymbolLogo%22%3Atrue%2C%22showFloatingTooltip%22%3Afalse%2C%22tabs%22%3A%5B%7B%22title%22%3A%22Futures%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22CME_MINI%3AES1%21%22%2C%22d%22%3A%22S%26P%20500%22%7D%2C%7B%22s%22%3A%22CME_MINI%3ANQ1%21%22%2C%22d%22%3A%22Nasdaq%22%7D%2C%7B%22s%22%3A%22NYMEX%3ACL1%21%22%2C%22d%22%3A%22Crude%20Oil%22%7D%2C%7B%22s%22%3A%22COMEX%3AGC1%21%22%2C%22d%22%3A%22Gold%22%7D%5D%7D%5D%7D"
                  title="market-overview"
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
